// The guided workout's own voice (pt only): Piper's pt_BR "Faber" (CC0), generated in the visitor's browser.
// The first workout downloads it (about 97 MB with its runtime) behind a progress bar; the files stay in the
// browser's Cache Storage, so later workouts only load them. Lines are made in a worker (piper.worker.js) and
// played through Web Audio as ambient sound, so they mix with the music the person trains to. Never through an audio
// element: on an iPhone that takes over the phone's audio and pauses Spotify / YouTube (28/10/2026). The price is the
// iPhone's silent switch, which mutes ambient sound: the start card asks iPhone users to turn it off.
// Anything that fails leaves the device voice (src/utils/speech.js) in charge.
import { withGain } from './gain';
import ortWasmUrl from 'onnxruntime-web-dist/ort-wasm-simd-threaded.wasm?url';
import phonemizerWasmUrl from '@diffusionstudio/piper-wasm/build/piper_phonemize.wasm?url';
import phonemizerDataUrl from '@diffusionstudio/piper-wasm/build/piper_phonemize.data?url';

export const VOICE_ID = 'pt_BR-faber-medium';

// The voice is served by the site (scripts/fetch-voice.sh); the runtime files are hashed build assets.
// Sizes are the fallback for the progress bar when a response comes compressed, without a Content-Length.
export const VOICE_FILES = [
  { key: 'model', url: `/voices/${VOICE_ID}.onnx`, size: 63201294 },
  { key: 'modelConfig', url: `/voices/${VOICE_ID}.onnx.json`, size: 4855 },
  { key: 'ortWasm', url: ortWasmUrl, size: 14848852 },
  { key: 'phonemizerWasm', url: phonemizerWasmUrl, size: 635212 },
  { key: 'phonemizerData', url: phonemizerDataUrl, size: 18077249 },
];

const CACHE_NAME = 'mt-voice-v1';
// Made lines kept for replay and prefetch (a workout says a few dozen)
const MAX_LINES = 60;

let worker = null;
let ready = false;
let preparing = null;
let nextId = 0;
const pending = new Map();
const lines = new Map();
let audioContext = null;
let playing = null;
let playToken = 0;
let gain = 1;

export const canUseVoice = (language) =>
  language === 'pt' &&
  typeof window !== 'undefined' &&
  typeof WebAssembly === 'object' &&
  typeof Worker === 'function' &&
  'caches' in window &&
  typeof (window.AudioContext || window.webkitAudioContext) === 'function';

export const isVoiceReady = () => ready;

// Bytes still to download (0 once every file is in the browser): the size the visitor is asked about first
export const voiceDownloadBytes = async () => {
  try {
    const cache = await caches.open(CACHE_NAME);
    const hits = await Promise.all(VOICE_FILES.map(({ url }) => cache.match(url)));
    return VOICE_FILES.reduce((sum, file, index) => (hits[index] ? sum : sum + file.size), 0);
  } catch {
    return VOICE_FILES.reduce((sum, file) => sum + file.size, 0);
  }
};

// True when every file is already in the browser (the next workout only loads them)
export const isVoiceStored = async () => (await voiceDownloadBytes()) === 0;

const download = async (cache, file, onBytes, signal) => {
  const response = await fetch(file.url, { signal });
  if (!response.ok || !response.body) throw new Error(`voice file ${file.url}: HTTP ${response.status}`);
  const reader = response.body.getReader();
  const chunks = [];
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    onBytes(value.length);
  }
  const blob = new Blob(chunks, { type: response.headers.get('Content-Type') || 'application/octet-stream' });
  await cache.put(file.url, new Response(blob, { headers: { 'Content-Type': blob.type } }));
};

const call = (message, transfer = []) =>
  new Promise((resolve, reject) => {
    const id = ++nextId;
    pending.set(id, { resolve, reject });
    worker.postMessage({ ...message, id }, transfer);
  });

const startWorker = () => {
  worker = new Worker(new URL('./piper.worker.js', import.meta.url), { type: 'module' });
  worker.onmessage = ({ data }) => {
    const job = pending.get(data.id);
    if (!job) return;
    pending.delete(data.id);
    if (data.ok) job.resolve(data);
    else job.reject(new Error(data.error));
  };
  worker.onerror = (event) => {
    pending.forEach(({ reject }) => reject(new Error(event.message || 'voice worker failed')));
    pending.clear();
  };
};

/**
 * Downloads what's missing (onProgress gets { loaded, total } in bytes, only while something downloads), then loads
 * the voice. Resolves once lines can be made; rejects on any failure (the caller keeps the device voice).
 */
export const prepareVoice = ({ onProgress, signal } = {}) => {
  if (ready) return Promise.resolve();
  if (preparing) return preparing;

  preparing = (async () => {
    const cache = await caches.open(CACHE_NAME);
    // Files from an earlier runtime (a new build with other hashes) would only take space
    const current = new Set(VOICE_FILES.map(({ url }) => new URL(url, window.location.href).href));
    for (const request of await cache.keys()) {
      if (!current.has(request.url)) await cache.delete(request);
    }

    const stored = await Promise.all(VOICE_FILES.map(({ url }) => cache.match(url)));
    const missing = VOICE_FILES.filter((_, index) => !stored[index]);
    if (missing.length) {
      const total = VOICE_FILES.reduce((sum, file) => sum + file.size, 0);
      const progress = { loaded: VOICE_FILES.filter((_, index) => stored[index]).reduce((sum, file) => sum + file.size, 0), total };
      const onBytes = (bytes) => {
        progress.loaded = Math.min(progress.loaded + bytes, total);
        onProgress?.({ ...progress });
      };
      onProgress?.({ ...progress });
      for (const file of missing) {
        await download(cache, file, onBytes, signal);
      }
    }

    const files = {};
    for (const { key, url } of VOICE_FILES) {
      const response = await cache.match(url);
      files[key] = key === 'modelConfig' ? await response.json() : await response.arrayBuffer();
    }
    if (signal?.aborted) throw new DOMException('aborted', 'AbortError');

    startWorker();
    await call({ type: 'init', files }, [files.model, files.ortWasm, files.phonemizerWasm, files.phonemizerData]);
    ready = true;
  })();

  preparing.catch(() => {
    preparing = null;
    worker?.terminate();
    worker = null;
  });
  return preparing;
};

// The voice volume as a gain (utils/voiceVolume.js); lines already playing keep theirs
export const setVoiceGain = (value) => {
  gain = value;
};

// Called from the tap that starts the workout: browsers only let audio start from a user gesture
export const unlockVoiceAudio = () => {
  try {
    // Safari (iOS 16.4+): say it outright, the page's audio is ambient sound that mixes with the person's music
    if (navigator.audioSession) navigator.audioSession.type = 'ambient';
  } catch {
    // older browsers: Web Audio is ambient anyway
  }
  try {
    if (!audioContext) audioContext = new (window.AudioContext || window.webkitAudioContext)();
    resumeIfSuspended(audioContext);
  } catch {
    audioContext = null;
  }
};

/**
 * iOS suspends a page's audio after a call, Siri, a locked screen or another app ("suspended" / "interrupted"): asks
 * for it back before a line plays. Resolves either way, after at most a second, so a refusal never holds a line up.
 */
export const resumeIfSuspended = (context) => {
  if (!context || context.state === 'running') return Promise.resolve();
  const resumed = Promise.resolve()
    .then(() => context.resume())
    .catch(() => {});
  return Promise.race([resumed, new Promise((done) => setTimeout(done, 1000))]);
};

// Makes a line ahead of time (the next set's, during the rest); resolves to its audio
export const prepareLine = (text) => {
  if (!ready || !text) return Promise.resolve(null);
  if (!lines.has(text)) {
    if (lines.size >= MAX_LINES) lines.delete(lines.keys().next().value);
    const made = call({ type: 'speak', text });
    made.catch(() => lines.delete(text));
    lines.set(text, made);
  }
  return lines.get(text);
};

export const stopVoice = () => {
  playToken += 1;
  if (playing) {
    try {
      playing.stop();
    } catch {
      // already ended
    }
    playing = null;
  }
};

// Plays a line, cutting off whatever was playing (like speechSynthesis.cancel + speak)
export const playLine = async (text) => {
  stopVoice();
  const token = playToken;
  const line = await prepareLine(text);
  if (!line || token !== playToken || !audioContext) return;
  await resumeIfSuspended(audioContext);
  if (token !== playToken || gain <= 0) return;
  const pcm = gain === 1 ? line.pcm : withGain(line.pcm, gain, line.sampleRate);
  const buffer = audioContext.createBuffer(1, pcm.length, line.sampleRate);
  buffer.copyToChannel(pcm, 0);
  const source = audioContext.createBufferSource();
  source.buffer = buffer;
  source.connect(audioContext.destination);
  source.onended = () => {
    if (playing === source) playing = null;
  };
  playing = source;
  source.start();
};
